import { useState } from 'react';

export default function TokenList({ tokens, onRevoke, onExtend, onDelete, onRefresh, onUpdate }) {
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    email: '',
    company: '',
    name: '',
    file_name: '',
    download_filename: ''
  });

  const copyLink = (token) => {
    const link = `${window.location.origin}/cv/view?token=${token}`;
    navigator.clipboard.writeText(link);
    alert('Lien copié dans le presse-papiers !');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff < 0) return 'Expiré';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}j ${hours % 24}h restantes`;
    }
    return `${hours}h ${minutes}min restantes`;
  };

  const handleEdit = (token) => {
    setEditingId(token.id);
    setEditForm({
      email: token.email,
      company: token.company,
      name: token.name,
      file_name: token.file_name || 'cv.pdf',
      download_filename: token.download_filename || 'CV.pdf'
    });
  };

  const handleSaveEdit = async () => {
    if (onUpdate) {
      await onUpdate(editingId, editForm);
      setEditingId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({
      email: '',
      company: '',
      name: '',
      file_name: '',
      download_filename: ''
    });
  };

  if (!tokens || tokens.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          Aucun token actif
        </h3>
        <p className="text-gray-500">
          Générez un nouveau lien pour commencer
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          📋 Tokens actifs ({tokens.length})
        </h2>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Actualiser</span>
        </button>
      </div>

      <div className="space-y-4">
        {tokens.map((token) => {
          const expired = isExpired(token.token_expires_at);
          const revoked = token.is_revoked;
          const isExpanded = expandedId === token.id;

          return (
            <div
              key={token.id}
              className={`border rounded-lg p-4 transition-all ${
                revoked ? 'border-orange-200 bg-orange-50' :
                expired ? 'border-red-200 bg-red-50' : 
                'border-gray-200 bg-white'
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className={`text-2xl ${expired || revoked ? 'opacity-50' : ''}`}>
                    {revoked ? '🚫' : expired ? '🔴' : '🟢'}
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {token.name}
                      {revoked && <span className="ml-2 text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded">RÉVOQUÉ</span>}
                    </h3>
                    <p className="text-sm text-gray-600">{token.company}</p>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : token.id)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className={`w-6 h-6 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Mode édition */}
              {editingId === token.id ? (
                <div className="space-y-3 mb-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">✏️ Modifier le token</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      className="px-3 py-2 border rounded"
                      placeholder="Email"
                    />
                    <input
                      type="text"
                      value={editForm.company}
                      onChange={(e) => setEditForm({...editForm, company: e.target.value})}
                      className="px-3 py-2 border rounded"
                      placeholder="Entreprise"
                    />
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      className="px-3 py-2 border rounded"
                      placeholder="Nom"
                    />
                    <input
                      type="text"
                      value={editForm.download_filename}
                      onChange={(e) => setEditForm({...editForm, download_filename: e.target.value})}
                      className="px-3 py-2 border rounded"
                      placeholder="Nom téléchargement"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSaveEdit}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      ✅ Sauvegarder
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      ❌ Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Email */}
                  <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>{token.email}</span>
                  </div>

                  {/* Fichier PDF */}
                  {token.file_name && (
                <div className="flex items-center space-x-2 mb-2">
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {token.file_name === 'cv-senior.pdf' && '🎯 CV Senior'}
                    {token.file_name === 'cv-junior.pdf' && '🌱 CV Junior'}
                    {token.file_name === 'portfolio.pdf' && '🎨 Portfolio'}
                    {token.file_name === 'cv.pdf' && '📄 CV Standard'}
                    {!['cv.pdf', 'cv-senior.pdf', 'cv-junior.pdf', 'portfolio.pdf'].includes(token.file_name) && `📁 ${token.file_name}`}
                  </span>
                  {token.download_filename && (
                    <span className="text-xs text-gray-500">
                      → {token.download_filename}
                    </span>
                  )}
                </div>
              )}
                </>
              )}

              {/* Statut et temps restant */}
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm font-medium ${
                  revoked ? 'text-orange-600' : 
                  expired ? 'text-red-600' : 
                  'text-green-600'
                }`}>
                  {revoked ? 'Token révoqué (inutilisable)' : getTimeRemaining(token.token_expires_at)}
                </span>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>👁️ {token.view_count || 0} vues</span>
                  <span>📥 {token.download_count || 0} DL</span>
                </div>
              </div>

              {/* Détails expandables */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2 text-sm text-gray-600">
                  <p><strong>Créé le :</strong> {formatDate(token.created_at)}</p>
                  <p><strong>Expire le :</strong> {formatDate(token.token_expires_at)}</p>
                  <p><strong>ID :</strong> {token.id}</p>
                  <div className="mt-2">
                    <p className="font-semibold mb-1">Token (tronqué) :</p>
                    <code className="text-xs bg-gray-100 p-2 rounded block break-all">
                      {token.token.substring(0, 50)}...
                    </code>
                  </div>
                </div>
              )}

              {/* Actions */}
              {editingId !== token.id && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(token)}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Éditer</span>
                  </button>

                  <button
                    onClick={() => copyLink(token.token)}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center space-x-1 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copier lien</span>
                  </button>

                {!expired && !revoked && (
                  <button
                    onClick={() => onExtend(token.id)}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Prolonger +48h</span>
                  </button>
                )}

                {!revoked && (
                  <button
                    onClick={() => {
                      if (confirm(`Voulez-vous vraiment révoquer le token de ${token.name} ?`)) {
                        onRevoke(token.id);
                      }
                    }}
                    className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-1 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    <span>Révoquer</span>
                  </button>
                )}

                <button
                  onClick={() => {
                    if (confirm(`⚠️ ATTENTION : Voulez-vous vraiment SUPPRIMER définitivement le token de ${token.name} ?\n\nCette action est irréversible !`)) {
                      onDelete(token.id);
                    }
                  }}
                  className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 flex items-center space-x-1 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Supprimer</span>
                </button>
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
